import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getTargets, setTarget } from "@/lib/dashboardApi";
import { inr } from "@/lib/format";

const DAYS = [
  { value: "0", label: "Monday" },
  { value: "1", label: "Tuesday" },
  { value: "2", label: "Wednesday" },
  { value: "3", label: "Thursday" },
  { value: "4", label: "Friday" },
  { value: "5", label: "Saturday" },
  { value: "6", label: "Sunday" },
];

export default function SalesTargetModal({ open, onClose, token, onSaved }) {
  const [targets, setTargets] = useState([]);
  const [mode, setMode] = useState("dow"); // 'dow' or 'date'
  const [dayOfWeek, setDayOfWeek] = useState("0");
  const [specificDate, setSpecificDate] = useState("");
  const [targetAmount, setTargetAmount] = useState("");
  const [targetOrders, setTargetOrders] = useState("");
  const [targetLabourHours, setTargetLabourHours] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const list = await getTargets(token);
      setTargets(list || []);
    } catch (err) {
      console.error("Failed to load targets:", err);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    if (!targetAmount || Number(targetAmount) <= 0) {
      setError("Target amount must be greater than zero");
      return;
    }
    if (mode === "date" && !specificDate) {
      setError("Please select a specific date override");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        target_amount: Number(targetAmount),
        target_orders: targetOrders ? parseInt(targetOrders, 10) : null,
        target_labour_hours: targetLabourHours ? Number(targetLabourHours) : null,
        notes: notes || null,
        day_of_week: mode === "dow" ? parseInt(dayOfWeek, 10) : null,
        specific_date: mode === "date" ? specificDate : null,
      };
      await setTarget(payload, token);
      await load();
      if (onSaved) onSaved();
      setTargetAmount("");
      setNotes("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save sales target");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Sales & Productivity Targets</DialogTitle>
          <DialogDescription>
            Set baseline weekday targets or specific-date overrides (e.g. festivals, New Year’s Eve).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-base rounded-md border p-base bg-muted/20">
          <div className="font-semibold text-meta">Add or Update Target</div>

          {error && <p className="text-meta text-danger">{error}</p>}

          <div className="grid grid-cols-2 gap-base">
            <div>
              <Label className="text-[11px]">Target Scope</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dow">Day of Week (Recurring)</SelectItem>
                  <SelectItem value="date">Specific Date Override</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {mode === "dow" ? (
              <div>
                <Label className="text-[11px]">Weekday</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-[11px]">Calendar Date</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={specificDate}
                  onChange={(e) => setSpecificDate(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-base">
            <div>
              <Label className="text-[11px]">Target Sales (₹) *</Label>
              <Input
                type="number"
                step="100"
                className="h-9"
                placeholder="50000"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                required
              />
            </div>
            <div>
              <Label className="text-[11px]">Target Orders</Label>
              <Input
                type="number"
                className="h-9"
                placeholder="120"
                value={targetOrders}
                onChange={(e) => setTargetOrders(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-[11px]">Target Labour Hrs</Label>
              <Input
                type="number"
                step="0.5"
                className="h-9"
                placeholder="40"
                value={targetLabourHours}
                onChange={(e) => setTargetLabourHours(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px]">Notes / Event Description</Label>
            <Input
              className="h-9"
              placeholder="e.g. IPL Match Night or Regular Saturday"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex justify-end">
            <Button size="sm" type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save Target"}
            </Button>
          </div>
        </form>

        {/* Existing Targets Table */}
        <div className="space-y-snug">
          <div className="font-semibold text-meta">Current Active Targets</div>
          <div className="rounded-md border divide-y text-meta max-h-48 overflow-y-auto">
            {targets.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-snug">
                <div>
                  <span className="font-semibold text-foreground">
                    {t.specific_date
                      ? `Date: ${t.specific_date}`
                      : DAYS.find((d) => d.value === String(t.day_of_week))?.label || `Day ${t.day_of_week}`}
                  </span>
                  {t.notes && <span className="ml-snug text-muted-foreground text-[11px]">({t.notes})</span>}
                </div>
                <div className="flex items-center gap-base tabular-nums">
                  <span className="font-bold text-foreground">{inr(t.target_amount)}</span>
                  {t.target_orders && <span className="text-muted-foreground">{t.target_orders} ord</span>}
                  {t.target_labour_hours && <span className="text-muted-foreground">{t.target_labour_hours}h</span>}
                </div>
              </div>
            ))}
            {targets.length === 0 && (
              <div className="p-base text-center text-muted-foreground">No targets configured yet.</div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
